import { useContext } from "react";
import { Navigate, useParams } from "react-router-dom";

import LoadingSpinner from "../components/loading-spinner";

import SurveySplashDataProvider, { SurveySplashDataContext } from "../providers/survey-splash-data";
import SurveyRespondentDataProvider, {
  SurveyRespondentDataContext,
} from "../providers/survey-respondent-data";
import SurveyProvider from "../providers/survey";
import SurveyValidInputProvider from "../providers/survey-valid-input";
import SurveyQuestionsVisibilityProvider from "../providers/survey-questions-visibility";
import SurveyNavigationProvider from "../providers/survey-navigation";

import useApiRequest from "../hooks/use-api-request";

import { API_REQUESTS } from "../constants/types";
import { SURVEY_STATUS } from "../constants/survey_status";

export const ProvideSplashScreen = ({ children }) => {
  const { surveyRoute } = useParams();
  const { data, loading, statusCode } = useApiRequest({
    request: API_REQUESTS.GET_SPLASH_PAGE_BY_ROUTE,
    params: { surveyRoute },
  });
  const exists = !loading && statusCode === 200;

  if (loading) {
    return <LoadingSpinner />;
  }
  return exists ? (
    <SurveySplashDataProvider data={data}>{children}</SurveySplashDataProvider>
  ) : (
    <Navigate to="/encuestas/no-existe" state={{}} />
  );
};

export const ProvideRespondentData = ({ children }) => {
  const { surveyMetadata } = useContext(SurveySplashDataContext);
  const { surveyId, identificationType, needsValidation, requiresPassword } = surveyMetadata;

  return (
    <SurveyRespondentDataProvider
      data={{ surveyId, identificationType, needsValidation, requiresPassword }}
    >
      {children}
    </SurveyRespondentDataProvider>
  );
};

export const ProvideSurvey = ({ children }) => {
  const { surveyId, respondentId, respondentIdType } = useContext(SurveyRespondentDataContext);
  const parameters = {
    survey_id: surveyId,
    identificator_type: respondentIdType,
    value: respondentId,
  };

  const { data: surveyData, loading: loadingSurveyData } = useApiRequest({
    request: API_REQUESTS.GET_SURVEY_BY_ID,
    params: { surveyId },
  });
  const { data: dbAnswers, loading: loadingDbAnswers } = useApiRequest({
    request: API_REQUESTS.CHECK_USER_ANSWERS,
    options: {
      method: "POST",
      body: JSON.stringify(parameters),
    },
  });

  if (loadingSurveyData || loadingDbAnswers) {
    return <LoadingSpinner />;
  }
  return (
    <SurveyProvider data={{ surveyData, dbAnswers }}>
      <SurveyQuestionsVisibilityProvider>
        <SurveyNavigationProvider>
          <SurveyValidInputProvider>{children}</SurveyValidInputProvider>
        </SurveyNavigationProvider>
      </SurveyQuestionsVisibilityProvider>
    </SurveyProvider>
  );
};

export const CheckForClosedSurvey = ({ children }) => {
  const { surveyMetadata } = useContext(SurveySplashDataContext);
  const { status } = surveyMetadata;

  return status ? { ...children } : <Navigate to="/encuestas/cerrada" state={{}} />;
};

export const CheckForPendingSurvey = ({ children }) => {
  const { surveyStatus } = useContext(SurveyRespondentDataContext);

  return surveyStatus === SURVEY_STATUS.PENDING || surveyStatus === SURVEY_STATUS.AWAITING_LOGIN ? (
    { ...children }
  ) : (
    <Navigate to="/encuestas/encuesta-completada" state={{}} />
  );
};

export const RedirectWhenUserNotIdentified = ({ children, redirectLink }) => {
  const { respondentId, isAnonymousSurvey } = useContext(SurveyRespondentDataContext);

  if (!isAnonymousSurvey && !respondentId) {
    return <Navigate to={redirectLink} />;
  }

  return children;
};
